import { useCallback, useEffect, useMemo } from "react";

export const useContentWithProcessedScriptTags = (content, { allowScriptTags }) => {
  // Gets a script element from another document and duplicates it to the current
  // document.
  const moveScriptElementToDocument = useCallback(
    sourceScriptElement => {
      // Remove the source element from the original document
      sourceScriptElement.remove();
      // If script tags are not allowed in the provided content, dont duplicate the source
      // element.
      if (!allowScriptTags) return;

      // Create a script element in the current document, which will allow it to be run when
      // appended to the head or body of the document.
      const scriptElement = document.createElement("script");
      // Duplicate all attributes from the source element to the target one
      Array.from(sourceScriptElement.attributes).forEach(({ name, value }) => {
        scriptElement.setAttribute(name, value);
      });

      // Return the new created element so it can be appended inside the current document
      // later.
      return scriptElement;
    },
    [allowScriptTags]
  );

  const [cleanedContent, scriptElements] = useMemo(() => {
    // Don't process anything if there's no string content
    if (!content?.length) return ["", []];

    // Parse the input content string into a new DOM document.
    const parser = new DOMParser();
    const parsedContent = parser.parseFromString(content, "text/html");

    // Remove all script tags from the document, and, if allowed, duplicate them to the
    // document to be appended later to the <head> tag.
    const sourceScriptElements = parsedContent.querySelectorAll("script");
    const scriptElements = Array.from(sourceScriptElements).reduce(
      (elements, sourceElement) => {
        const targetElement = moveScriptElementToDocument(sourceElement);
        return targetElement ? [...elements, targetElement] : elements;
      },
      []
    );
    // Return the cleaned HTML and the list of duplicated script elements. Note that if
    // script tags are not allowed, the `scriptElements` variable is an empty array.
    return [parsedContent.body.innerHTML, scriptElements];
  }, [content, moveScriptElementToDocument]);

  // Append the script tags to the document's `<head>` tag so that they are executed by
  // the browser, and remove them when the component is unmounted or the content is
  // replaced.
  useEffect(() => {
    if (!allowScriptTags || !scriptElements.length) return;

    scriptElements.forEach(element => {
      document.head.appendChild(element);
    });

    return () => {
      scriptElements.forEach(element => {
        document.head.removeChild(element);
      });
    };
  }, [allowScriptTags, scriptElements]);

  // The returned content is cleaned from all `<script>` elements, as `<script>` elements
  // processing is handled, if allowed, by the `useEffect` hook above.
  return cleanedContent;
};
